// InvoiceGroupCoverLetterPdf.tsx
import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';

/* ============================
   Types
============================ */

export interface IInvoiceGroupCoverLetterDocument {
  letterDate: string;            // full datetime string
  groupIrNumber: string;
  clientName: string;
  clientContact: string;
  clientAddress: string;
  clientAddress2: string;
  clientAddress3: string;
  clientCity: string;
  clientState: string;
  clientZip: string;
  salutation: string;
  firstParagraph: string;
  secondParagraph: string;
  signatureTitle: string;
  projectName: string;
  wellName: string;
  parcelNumbers: string;
  qlsNumber: string;
  afeNumber: string;
  department: string;
  municipalities: string;
  signatureName: string;
  topRightElements?: string[];
}

/* ============================
   Constants / Fonts
============================ */

const neonGreen = '#2EDD12';
const pagePadding = 20;

Font.register({
  family: 'Impact',
  src: '/fonts/Impact.ttf'
});

/* ============================
   Helpers
============================ */

const formatDateOnly = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  });

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

/* ============================
   Styles
============================ */

const styles = StyleSheet.create({
  page: {
    padding: `0 ${pagePadding} ${pagePadding} ${pagePadding}`,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },

  /* Top bar */
  topGreenBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 18,
    backgroundColor: neonGreen,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: 16,
  },
  topGreenText: {
    fontSize: 9,
    marginLeft: 12,
  },

  /* Letter container */
  letterContainer: {
    marginTop: 42,
    paddingHorizontal: pagePadding,
  },

  topRightDateTime: {
    textAlign: 'right',
    fontSize: 9,
    marginBottom: 10,
  },

  logoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },

  logoText: {
    fontFamily: 'Impact',
    fontSize: 48,
    lineHeight: 1,
  },
  logoSub: {
    fontFamily: 'Impact',
    fontSize: 14,
    letterSpacing: 1,
  },

  groupBox: {
    backgroundColor: '#000',
    paddingVertical: 6,
    paddingHorizontal: 10,
    width: 170,
    alignItems: 'center',
    justifyContent: 'center',
  },
  groupBoxText: {
    color: '#FFF',
    fontSize: 11,
    textAlign: 'center',
  },

  letterBody: {
    lineHeight: 1.4,
  },
  block: {
    marginBottom: 10,
  },
  signature: {
    marginTop: 16,
  },

  /* Footer */
  footer: {
    position: 'absolute',
    bottom: -pagePadding, // e.g., bottom: -40
    left: -pagePadding,
    right: -pagePadding,
    height: 72,
    backgroundColor: '#000000',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    padding: '10 30 30 10'
  },
  leftText: {
    color: 'white',
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 5,
    marginLeft: 20,
    paddingBottom: 5
  },
  leftHeading: {
    color: '#39FF14', // neon green
    fontWeight: 'bold',
    fontSize: 10,
    marginBottom: 10,
    marginLeft: 20
  },
  rightBlock: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingRight: 20
  },
  rightHeading: {
    color: '#39FF14',
    fontWeight: 'bold',
    fontSize: 10,
  },
  rightSub: {
    color: 'white',
    fontSize: 14,
  },
  footerHeading: {
    color: neonGreen,
    fontWeight: 'bold',
    fontSize: 10,
    lineHeight: 13,
    marginBottom: 6,
  },
  footerText: {
    color: '#FFF',
    fontSize: 9,
    lineHeight: 12,
    marginBottom: 4
  },
  metaBlock: {
    marginTop: 8,
    marginBottom: 8
  },
  metaRow: {
    fontSize: 11,
    marginBottom: 6,
    lineHeight: 1.3
  },
  metaLabel: {
    fontFamily: 'Helvetica-Bold',
    fontWeight: 'extrabold'
  },
  metaText: {
    fontWeight: 100,
  }
});

/* ============================
   Footer
============================ */

const InvoiceFooter = () => (
    <View style={styles.footer}>
      <View>
        <Text style={styles.leftHeading}>PLEASE REMIT TO:</Text>
        <Text style={styles.leftText}>5925 N. Robinson Ave</Text>
        <Text style={styles.leftText}>Oklahoma City, OK 73118</Text>
        <Text style={styles.leftText}>(405) 848-4649</Text>
      </View>
      <View style={styles.rightBlock}>
        <Text style={styles.rightSub}>Delivering Energy Through Land</Text>
        <Text style={styles.rightHeading}>
          PEOPLE | SERVICE | PRODUCT | EXAMPLE
        </Text>
      </View>
    </View>
); 

/* ============================
   Document
============================ */

const InvoiceGroupCoverLetterPdf: React.FC<{ invoice: IInvoiceGroupCoverLetterDocument }> = ({ invoice }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>

      {/* Top green bar */}
      <View style={styles.topGreenBar}>
        {invoice.topRightElements?.map((el, i) => (
          <Text key={i} style={styles.topGreenText}>{el}</Text>
        ))}
      </View>

      {/* Letter content */}
      <View style={styles.letterContainer}>

        {/* DateTime */}
        <Text style={styles.topRightDateTime}>
          {formatDateTime(invoice.letterDate)}
        </Text>

        {/* Logo + Group Box */}
        <View style={styles.logoRow}>
          <View>
            <Text style={styles.logoText}>DUDLEY</Text>
            <Text style={styles.logoSub}>LAND COMPANY</Text>
          </View>

          <View style={styles.groupBox}>
            <Text style={styles.groupBoxText}>
              Group #: {invoice.groupIrNumber}
            </Text>
            <Text style={styles.groupBoxText}>
              {formatDateOnly(invoice.letterDate)}
            </Text>
          </View>
        </View>

        {/* Letter Body */}
        <View style={styles.letterBody}>
          <Text style={styles.block}>{formatDateOnly(invoice.letterDate)}</Text>

          <View style={styles.block}>
            <Text>{invoice.clientName}</Text>
            <Text>Attn: {invoice.clientContact}</Text>
            <Text>{invoice.clientAddress}</Text>
            <Text>{invoice.clientAddress2}</Text>
            <Text>{invoice.clientAddress3}</Text>
            <Text>
              {invoice.clientCity}, {invoice.clientState} {invoice.clientZip}
            </Text>
          </View>

          <Text style={styles.block}>{invoice.salutation},</Text>

          <Text style={styles.block}>
            {invoice.firstParagraph}
          </Text>

          <View style={styles.metaBlock}>
            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>Name: </Text>
              <Text style={styles.metaText}>{invoice.projectName}</Text>
            </Text>

            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>Well Name: </Text>
              <Text style={styles.metaText}>{invoice.wellName}</Text>
            </Text>

            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>Parcel Number(s): </Text>
              <Text style={styles.metaText}>{invoice.parcelNumbers}</Text>
            </Text>

            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>QLS Number(s): </Text>
              <Text style={styles.metaText}>{invoice.qlsNumber}</Text>
            </Text>

            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>AFE Number: </Text>
              <Text style={styles.metaText}>{invoice.afeNumber}</Text>
            </Text>

            <Text style={styles.metaRow}>
              <Text style={styles.metaLabel}>Department to Bill to: </Text>
              <Text style={styles.metaText}>{invoice.department}</Text>
            </Text>
          </View>

          <Text style={styles.block}>
            {invoice.secondParagraph}
          </Text>

          <View style={styles.block}>
            <Text>Sincerely,</Text>
          </View>

          <View style={styles.signature}>
            <Text>{invoice.signatureName}</Text>
            <Text>{invoice.signatureTitle}</Text>
            <Text>encl.</Text>
          </View>
        </View>
      </View>

      <InvoiceFooter />
    </Page>
  </Document>
);

export default InvoiceGroupCoverLetterPdf;


// WEBPACK FOOTER //
// ./src/components/project-billing/components/InvoiceGroupCoverLetterPdf.tsx